import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';

// Availability responses can carry a note that changes how the ticket must be
// booked, e.g. is_alt_avail: "Book MAS to ADI, get off at KYN". These components
// make sure that note is impossible to miss.

const cleanLines = (text) => String(text || '').split('\n').map(s => s.trim()).filter(Boolean);

export const getAvailNote = (seatAvail) => {
    if (!seatAvail) return null;
    const alt = seatAvail.is_alt_avail === true;
    const lines = cleanLines(seatAvail.explanation);
    if (lines.length === 0 && !(alt && seatAvail.comment)) return null;
    return {
        alt,
        lines: lines.length ? lines : [seatAvail.comment],
        comment: seatAvail.comment || null,
        src: seatAvail.src || null,
        dest: seatAvail.dest || null,
        srcName: seatAvail.src_name || null,
        destName: seatAvail.dest_name || null,
        date: seatAvail.date || null,
        cls: seatAvail.seat_class || null,
        updated: seatAvail.updated_at_ui || null,
    };
};

const FONT = "'Inter', sans-serif";

// Full note, shown in the expanded leg above the seat classes
export const AvailabilityNote = ({ seatAvail, trainNum, style }) => {
    const note = getAvailNote(seatAvail);
    if (!note) return null;

    const palette = note.alt
        ? { bg: '#FFFBEB', border: '#F59E0B', bar: '#D97706', title: '#92400E', text: '#78350F', chipBg: '#FEF3C7' }
        : { bg: '#EFF6FF', border: '#BFDBFE', bar: '#3B82F6', title: '#1E40AF', text: '#1E3A8A', chipBg: '#DBEAFE' };
    const Icon = note.alt ? AlertTriangle : Info;
    const ticket = note.alt && (note.src || note.srcName) && (note.dest || note.destName);

    return (
        <div
            role={note.alt ? 'alert' : 'note'}
            onClick={(e) => e.stopPropagation()}
            style={{
                margin: '12px 0 4px',
                padding: '12px 14px',
                background: palette.bg,
                border: `1px solid ${palette.border}`,
                borderLeft: `4px solid ${palette.bar}`,
                borderRadius: '10px',
                fontFamily: FONT,
                cursor: 'default',
                ...style,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Icon size={18} color={palette.bar} strokeWidth={2.5} style={{ flexShrink: 0 }} aria-hidden="true" />
                <span style={{ fontSize: '14px', fontWeight: 800, color: palette.title }}>
                    {note.alt ? 'Important: book a different ticket for this train' : 'Availability note'}
                </span>
            </div>

            {note.lines.map((line, i) => (
                <p key={i} style={{ margin: i === 0 ? 0 : '4px 0 0', fontSize: '13px', lineHeight: 1.55, fontWeight: 500, color: palette.text }}>
                    {line}
                </p>
            ))}

            {ticket && (
                <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                    <span style={{ fontSize: '12px', fontWeight: 700, color: palette.title }}>Ticket to book on IRCTC:</span>
                    <span style={{ fontSize: '12px', fontWeight: 800, color: palette.text, background: palette.chipBg, padding: '3px 10px', borderRadius: '999px' }}>
                        {note.src || note.srcName} → {note.dest || note.destName}
                        {trainNum ? ` · ${trainNum}` : ''}
                        {note.date ? ` · ${note.date}` : ''}
                        {note.cls ? ` · ${note.cls}` : ''}
                    </span>
                </div>
            )}

            {note.updated && (
                <div style={{ marginTop: '6px', fontSize: '11px', color: palette.title, opacity: 0.75 }}>
                    Availability checked {note.updated}
                </div>
            )}
        </div>
    );
};

// Compact strip for the collapsed card / page summary. Only for alternate-booking
// notes, since those change what the user must buy.
// items: [{ trainNum, seatAvail }]
export const AltBookingBanner = ({ items, style }) => {
    const notes = (items || [])
        .map(({ trainNum, seatAvail }) => ({ trainNum, note: getAvailNote(seatAvail) }))
        .filter(x => x.note && x.note.alt);
    if (notes.length === 0) return null;

    return (
        <div
            role="alert"
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '8px 12px',
                background: '#FFFBEB',
                border: '1px solid #F59E0B',
                borderRadius: '8px',
                fontFamily: FONT,
                ...style,
            }}
        >
            {notes.map(({ trainNum, note }, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                    <AlertTriangle size={16} color="#D97706" strokeWidth={2.5} style={{ flexShrink: 0, marginTop: '1px' }} aria-hidden="true" />
                    <span style={{ fontSize: '13px', lineHeight: 1.45, color: '#78350F' }}>
                        <strong style={{ color: '#92400E' }}>Booking tip{trainNum ? ` (${trainNum})` : ''}:</strong>{' '}
                        {note.comment || note.lines[0]}
                    </span>
                </div>
            ))}
        </div>
    );
};

export default AvailabilityNote;
